/** The staff toggle. Every footfall metric the API returns comes with a
 *  staff twin (`staff_entries`, `staff_female`, …): how many of that count
 *  were enrolled employees. The API never subtracts; the console does,
 *  here, when the FilterBar's Advanced › "Staff Face ID Exclusion" is on. */
import type { HourOfDayPoint, MetricTotals } from "../types";

const METRICS = [
  "entries",
  "exits",
  "male",
  "female",
  "age_0_17",
  "age_18_29",
  "age_30_44",
  "age_45_plus",
  "unknown_demo",
] as const;

/** The same row with the staff twins taken out of every metric (never
 *  below zero). The twins themselves are kept, so a tile can still say how
 *  many were removed. */
export function withoutStaff<T extends MetricTotals>(row: T): T {
  const out = { ...row };
  for (const metric of METRICS) {
    const twin = `staff_${metric}` as const;
    out[metric] = Math.max(0, row[metric] - (row[twin] ?? 0)) as T[typeof metric];
  }
  return out;
}

/** Apply the toggle to a list of metric rows. */
export function applyStaff<T extends MetricTotals>(rows: T[], exclude: boolean): T[] {
  return exclude ? rows.map(withoutStaff) : rows;
}

/** The peak-hours series without staff: both the total and the average
 *  per hour drop by the staff share. */
export function hoursWithoutStaff<T extends HourOfDayPoint>(rows: T[], exclude: boolean): T[] {
  if (!exclude) return rows;
  return rows.map((row) => ({
    ...row,
    entries: Math.max(0, row.entries - (row.staff_entries ?? 0)),
    avg_entries: Math.max(0, row.avg_entries - (row.avg_staff_entries ?? 0)),
  }));
}
